import type { IndexUiState } from 'instantsearch.js'
import { useMemo } from 'react'
import {
  Configure,
  InstantSearch,
  type InstantSearchProps
} from 'react-instantsearch'
import createSearchClient from '../../../../assets/utils/algolia/createSearchClient'
import { DateFilterProvider } from '../context/DateFilterContext'
import useDetermineDateFilter from '../hooks/useDetermineDateFilter'
import {
  type IndexName,
  useConfigContext,
  useWidgetOptions
} from './ConfigContext'
import SearchUI from './SearchUI'

const COMPACT_VIEW_HITS_PER_PAGE = 40

const instantSearchProps = {
  future: {
    preserveSharedStateOnUnmount: true
  },
  insights: false
} as const satisfies Partial<InstantSearchProps>

type TUiState = {
  [Key in IndexName]: IndexUiState
}

export type AppProps<TRouteState = TUiState> = {
  routing?: InstantSearchProps<TUiState, TRouteState>['routing']
}

export default function App<TRouteState = TUiState>({
  routing
}: AppProps<TRouteState>) {
  const { appId, apiKey, postsPerPage, indexName } = useConfigContext()
  const searchClient = useMemo(() => {
    return createSearchClient(appId, apiKey)
  }, [appId, apiKey])

  const { compactView } = useWidgetOptions()
  const dateFilterState = useDetermineDateFilter(
    searchClient,
    indexName,
    compactView
  )

  const dateFilterValue = dateFilterState[0]

  // The compact view lists several weeks at once, so one page must hold more hits
  const hitsPerPage = compactView
    ? Math.max(postsPerPage, COMPACT_VIEW_HITS_PER_PAGE)
    : postsPerPage

  return (
    <DateFilterProvider state={dateFilterState}>
      <InstantSearch
        {...instantSearchProps}
        routing={routing}
        searchClient={searchClient}
        indexName={indexName}
      >
        <Configure
          hitsPerPage={dateFilterValue ? hitsPerPage : 0}
          getRankingInfo
          filters={
            dateFilterValue
              ? `date:${dateFilterValue[0]} TO ${dateFilterValue[1]}`
              : ''
          }
        />
        <SearchUI />
      </InstantSearch>
    </DateFilterProvider>
  )
}
